// Selection (05d) — which modules are selected (Ben's #173 box-select, #178 header click +
// Cmd/Ctrl+A, #186 Shift+drag). main.js kept it as the `.module-selected` class; it's the
// store's `selectedIds` now, and PlacedModule puts the class on from there.
//
// Selecting is never an undo step (as in main.js). Deleting the selection is — one step
// for the batch (useSelectionKeys).

import { flushSync } from "react-dom";
import { useProtoStore } from "../store/store.js";
import { moduleElementOf, moduleIdOf } from "./module-registry.js";

/** Below this many px a press on empty canvas is a click, not a box (main.js's value). */
export const BOX_DRAG_THRESHOLD = 4;

export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** The box between the press point and the pointer, whichever way it was dragged. */
export function boxBetween(x1: number, y1: number, x2: number, y2: number): Box {
  return { left: Math.min(x1, x2), top: Math.min(y1, y2), right: Math.max(x1, x2), bottom: Math.max(y1, y2) };
}

/** Overlap, not containment: touching a module with the box selects it. Edges that only
 *  meet don't count (main.js's strict `<` / `>`). */
export function boxHits(box: Box, r: Box): boolean {
  return r.left < box.right && r.right > box.left && r.top < box.bottom && r.bottom > box.top;
}

/**
 * main.js updateSelection: every module the box touches, plus the ones kept because
 * Shift was held when the drag started. Ids come back in module order.
 */
export function boxSelection(box: Box, modules: readonly { id: string; rect: Box }[], keep: ReadonlySet<string>): string[] {
  return modules.filter((m) => boxHits(box, m.rect) || keep.has(m.id)).map((m) => m.id);
}

// ── The live selection ───────────────────────────────────────────────────────
// Synchronous (flushSync), so the class is on the element as soon as the call returns —
// main.js toggled it in place, and its code (and the "Changes N" hint) reads it straight
// after. Only ever called from event handlers.

function commit(ids: readonly string[]): void {
  flushSync(() => useProtoStore.getState().setSelectedIds(ids));
}

function idsOf(mods: Iterable<HTMLElement>): string[] {
  const ids: string[] = [];
  for (const m of mods) {
    const id = moduleIdOf(m);
    if (id !== undefined) ids.push(id);
  }
  return ids;
}

/** What main.js's remaining code (the header press / drag, until 05e) calls. */
export interface SelectionApi {
  /** The selected module elements, in canvas order. */
  selectedModules: () => HTMLElement[];
  isSelected: (mod: HTMLElement) => boolean;
  /** A header click: select it, or unselect it if it was. */
  toggleSelected: (mod: HTMLElement) => void;
  /** A drag of an unselected module drops the old selection. */
  deselect: (mods: readonly HTMLElement[]) => void;
}

export function selectedModules(): HTMLElement[] {
  return useProtoStore
    .getState()
    .selectedIds.map((id) => moduleElementOf(id))
    .filter((m): m is HTMLElement => m !== undefined);
}

export function isSelected(mod: HTMLElement): boolean {
  const id = moduleIdOf(mod);
  return id !== undefined && useProtoStore.getState().selectedIds.includes(id);
}

export function toggleSelected(mod: HTMLElement): void {
  const id = moduleIdOf(mod);
  if (id === undefined) return; // already gone (closed mid-click) — nothing to toggle
  const current = useProtoStore.getState().selectedIds;
  commit(current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
}

export function deselect(mods: readonly HTMLElement[]): void {
  const drop = new Set(idsOf(mods));
  commit(useProtoStore.getState().selectedIds.filter((id) => !drop.has(id)));
}

/** Cmd/Ctrl+A: every module on the canvas. */
export function selectAll(): void {
  commit(useProtoStore.getState().modules.map((m) => m.id));
}

/** Set the selection to exactly these ids (the box-select drag). */
export function selectIds(ids: readonly string[]): void {
  commit(ids);
}

export const selectionApi: SelectionApi = { selectedModules, isSelected, toggleSelected, deselect };
